import { Explanation, type RowModel, useRowModel } from '@cancercentrum/rcc-react';
import classnames from 'classnames';
import { Badge, Panel } from '@cancercentrum/rcc-bootstrap';
import { type FC, type FormEventHandler, type ReactElement, useState } from 'react';
import { addDays, formatISO, parseISO } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import HTab from '../../../components/HTabs/HTab.tsx';
import Question from '../../components/IPOQuestion.tsx';
import type { Oversikt } from '../../../offline/muu2.0/form-oversikt/brostcancer/form.brostcancer.muu';
import { useIPO } from '../../hooks/useIPO.ts';
import { DisplayName, ModuleName } from './constants.ts';

const CustTabHeader = (props: {
    displayName: string;
    isDirty: boolean;
    count: number;
    className?: string;
}): ReactElement => {
    const { displayName, isDirty, count, className } = props;
    const name = count ? <span>{displayName} <Badge bsStyle="primary">{count}</Badge></span> : displayName;

    return (
        <div className={className}>
            {isDirty && (
                <span className="me-1">
                    <Explanation>Innehåller osparade ändringar</Explanation>
                </span>
            )}
            {name}
        </div>
    );
};

const RemovalDate = (props: {
    lastUpdated: Date;
}) => {
    const { lastUpdated } = props;

    return (
        <strong>{formatISO(addDays(lastUpdated, 7), { representation: 'date' })}</strong>
    );
};

export const EngangsregistreringarTab = (props: {
    dateShortname?: keyof Oversikt['regvars'];
    statusShortnames?: (keyof Oversikt['regvars'])[];
    extraShortnames?: (keyof Oversikt['regvars'])[];
    highlight?: boolean;
    components?: {
        Extra?: FC<{
            shortnames: (keyof Oversikt['regvars'])[];
            row: RowModel;
        }>;
    };
}) => {
    const { dateShortname, statusShortnames, extraShortnames, components, highlight } = props;
    const row = useRowModel();
    const ipo = useIPO();
    const radera = !!row.getRegvarValue('radera');
    const uppdaterad = row.getRegvarValue('uppdateradDatum');
    const [lastUpdated, setLastUpdated] = useState(() => parseISO(uppdaterad));

    const allShortnames: (keyof Oversikt['regvars'])[] = [
        ...(dateShortname ? [dateShortname] : []),
        ...(statusShortnames || []),
        ...(extraShortnames || []),
    ];

    const isDirty = !!row && allShortnames.some(x => row.getRegvar(x).$isDirty);
    const usedSettings = row ? allShortnames.filter(x => row.getRegvarValue(x)).length : 0;
    const Extra = components?.Extra;

    const header = (
        <CustTabHeader
            displayName={DisplayName}
            className={classnames({
                'tab-highlight': highlight,
            })}
            isDirty={isDirty}
            count={usedSettings}
        />
    );

    const onMarkForDeletion: FormEventHandler = () => {
        setLastUpdated(new Date());
    };

    return (
        <HTab eventKey={ModuleName} header={header}>
            <div className="my-4">
                {!!dateShortname && (
                    <Question regvar={row.getRegvar('senastKontrollerad')} />
                )}
                {!!statusShortnames && (
                    <fieldset className={dateShortname ? 'mt-4' : undefined}>
                        <legend className="text-label">Status för registrering</legend>

                        {statusShortnames.map(x => <Question key={x} regvar={row.getRegvar(x)} inline={true} />)}
                    </fieldset>
                )}
                {!!Extra && <Extra row={row} shortnames={extraShortnames || []} />}
                <hr />
                <Panel bsStyle="danger" style={{ width: '45rem' }}>
                    <Panel.Heading>
                        <div className="d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon={faTrashCan} />
                            <div>Markera för borttag</div>
                        </div>
                    </Panel.Heading>
                    <Panel.Body>
                        <div>
                            <p>Här kan du markera att en patient är felaktigt registrerad och ska tas bort från IPÖ, exempelvis om patienten registrerats med fel diagnos eller om utredning visat att patienten inte har den aktuella diagnosen.</p>
                            <p>Patienten tas bort tidigast en vecka efter att markeringen har gjorts. Under denna period har du möjlighet att ångra borttagningen.</p>

                            <Question regvar={row.getRegvar('radera')} inline={true} onValueChange={onMarkForDeletion} />

                            {radera && (
                                <div>
                                    <hr />
                                    <strong>{ipo.patient.ssn}</strong> är markerad för borttag från <strong>{ipo.diagnosis.diagnos}</strong> och kommer att tas bort tidigast <RemovalDate lastUpdated={lastUpdated} />.
                                </div>
                            )}
                        </div>
                    </Panel.Body>
                </Panel>
            </div>
        </HTab>
    );
};

export default EngangsregistreringarTab;
